import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import {
  api,
  useListOpsAppDocumentsQuery,
  useLazyListOpsAppDocumentsQuery,
  useGetOpsAppDocumentQuery,
  useLazyGetOpsAppDocumentQuery
} from "@webcore/services/api/api";
import { OpsAppDocument } from "@webcore/services/api/models";
import { isNotFoundError, OpsAppQueryResult } from "@webcore/services/opsapp/useOpsAppObjectsHostApi";
import { PdfPageSize, renderHtmlToPdf } from "@webcore/services/opsapp/htmlPdfRenderer";
import { buildOpsAppDocumentDownloadUrl, requestOpsAppDocumentDownload } from "@webcore/services/opsapp/documentDownload";

/** The bytes and identity of a document to store: a Blob, or text encoded as UTF-8. */
export interface OpsAppDocumentContent {
  fileName: string;
  contentType: string;
  content: Blob | string;
}

export interface RenderPdfInput {
  /** Self-contained HTML: inline CSS, data-URI images, no scripts or external URLs. */
  html: string;
  fileName: string;
  /** Defaults to Letter. */
  pageSize?: PdfPageSize;
  /** Page margin in points, applied on all sides. Defaults to 0 — let the document own its margins via CSS. */
  marginPt?: number;
}

export interface RenderPdfResult {
  documentId: string;
  /** The cookie-authenticated download route for the stored PDF. */
  url: string;
}

/** Fetches the team's documents on demand; resolves them newest first. */
export type OpsAppDocumentsListTrigger = () => Promise<OpsAppDocument[]>;

/** Fetches a document's metadata on demand; resolves null when it does not exist. */
export type OpsAppDocumentGetTrigger = (documentId: string) => Promise<OpsAppDocument | null>;

/**
 * The `opsAppDocuments` member of the custom view host scope: team-scoped file blobs a view can
 * create, read, replace, delete and hand to the user as a download. Mirrors the ambient
 * declarations custom view projects consume from their `src/globals.d.ts`. Views only.
 *
 * Reads follow the RTK Query pattern. Each read has a **live** hook (auto-updates when the team's
 * documents change) and a **lazy** hook (fetch on demand via the returned trigger, e.g. from an
 * event handler). Call the hooks at the top level of your view, following the rules of hooks.
 */
export interface OpsAppDocumentsHostApi {
  /** Live query for all of the team's documents, newest first. Auto-updates on every write. */
  useListOpsAppDocuments(): OpsAppQueryResult<OpsAppDocument[]>;
  /**
   * Live query for a single document's metadata by id. Auto-updates when the document changes. A
   * missing document resolves to `isError` (a 404); check `isError` rather than expecting
   * `data === undefined`.
   */
  useGetOpsAppDocument(documentId: string): OpsAppQueryResult<OpsAppDocument>;

  /**
   * Lazy version of {@link useListOpsAppDocuments}: returns `[fetchDocuments, result]`. Call
   * `fetchDocuments` on demand; `result` then auto-updates like the live query.
   */
  useLazyListOpsAppDocuments(): [OpsAppDocumentsListTrigger, OpsAppQueryResult<OpsAppDocument[]>];
  /**
   * Lazy version of {@link useGetOpsAppDocument}: returns `[fetchDocument, result]`.
   * `fetchDocument` resolves null when the document does not exist.
   */
  useLazyGetOpsAppDocument(): [OpsAppDocumentGetTrigger, OpsAppQueryResult<OpsAppDocument>];

  /** Stores a new document; the platform mints its id. Requires write capability. */
  create(input: OpsAppDocumentContent): Promise<OpsAppDocument>;
  /** Replaces an existing document's bytes and name in place. Requires write capability. */
  replace(documentId: string, input: OpsAppDocumentContent): Promise<OpsAppDocument>;
  /** Deletes a document; resolves whether it existed. Requires write capability. */
  remove(documentId: string): Promise<boolean>;
  /** Renders self-contained HTML to PDF in the browser and stores it as a document. */
  renderPdf(input: RenderPdfInput): Promise<RenderPdfResult>;
  /** Hands a stored document to the browser as a download; the host performs it outside any sandbox. */
  download(documentId: string): Promise<void>;
}

type ApiDispatch = ThunkDispatch<Record<string, unknown>, unknown, UnknownAction>;

function toBlob(content: Blob | string, contentType: string): Blob {
  return typeof content === "string" ? new Blob([content], { type: contentType }) : content;
}

// The host read hooks: thin wrappers over the shared RTK Query hooks that unwrap the list response
// to the documents themselves. Unlike the objects hooks, documents are team-scoped and carry no
// configuration id, so there is nothing to close over and the hooks are plain module-level
// functions. The view calls them inside its own render, so they resolve the host app's store
// context just like any RTK Query hook.

function useListDocuments(): OpsAppQueryResult<OpsAppDocument[]> {
  const result = useListOpsAppDocumentsQuery();
  const data = useMemo(() => result.data?.items, [result.data]);
  return { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch: result.refetch };
}

function useGetDocument(documentId: string): OpsAppQueryResult<OpsAppDocument> {
  const result = useGetOpsAppDocumentQuery({ documentId });
  return { data: result.data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch: result.refetch };
}

function useLazyListDocuments(): [OpsAppDocumentsListTrigger, OpsAppQueryResult<OpsAppDocument[]>] {
  // Lazy query state carries no refetch (lazy queries re-run through the trigger); refetch re-issues
  // the query, forcing a network round-trip (preferCacheValue: false). The list query takes no
  // arguments, so there is nothing to re-issue until it has been triggered once.
  const [trigger, result] = useLazyListOpsAppDocumentsQuery();
  const data = useMemo(() => result.data?.items, [result.data]);
  const fetchDocuments = useCallback<OpsAppDocumentsListTrigger>(
    async () => {
      const response = await trigger(undefined).unwrap();
      return response.items;
    },
    [trigger]
  );
  const isUninitialized = result.isUninitialized;
  const refetch = useCallback(() => {
    if (!isUninitialized) {
      void trigger(undefined, false);
    }
  }, [trigger, isUninitialized]);
  return [fetchDocuments, { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch }];
}

function useLazyGetDocument(): [OpsAppDocumentGetTrigger, OpsAppQueryResult<OpsAppDocument>] {
  const [trigger, result, { lastArg }] = useLazyGetOpsAppDocumentQuery();
  const fetchDocument = useCallback<OpsAppDocumentGetTrigger>(
    async (documentId) => {
      try {
        return await trigger({ documentId }).unwrap();
      } catch (error) {
        if (isNotFoundError(error)) {
          return null;
        }
        throw error;
      }
    },
    [trigger]
  );
  const refetch = useCallback(() => {
    if (lastArg !== undefined) {
      void trigger(lastArg, false);
    }
  }, [trigger, lastArg]);
  return [fetchDocument, { data: result.data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch }];
}

/**
 * Builds the `opsAppDocuments` host API injected into custom view code.
 *
 * Reads are the shared RTK Query hooks; writes are imperative mutations dispatched through the same
 * endpoints, so the `OpsAppDocuments` cache tag keeps the live hooks coherent with them — a create,
 * replace or delete invalidates the tag and every live read hook refetches and re-renders its
 * caller. `renderPdf` and `download` are not API round trips: the first renders in the browser and
 * then stores the result through `create`, the second hands the stored bytes to the host window.
 */
export function useOpsAppDocumentsHostApi(): OpsAppDocumentsHostApi {
  const dispatch = useDispatch<ApiDispatch>();

  return useMemo(() => {
    const create = async ({ fileName, contentType, content }: OpsAppDocumentContent): Promise<OpsAppDocument> =>
      await dispatch(api.endpoints.createOpsAppDocument.initiate(
        { fileName, contentType, content: toBlob(content, contentType) }
      )).unwrap();

    return {
      useListOpsAppDocuments: useListDocuments,
      useGetOpsAppDocument: useGetDocument,
      useLazyListOpsAppDocuments: useLazyListDocuments,
      useLazyGetOpsAppDocument: useLazyGetDocument,

      create,

      replace: async (documentId: string, { fileName, contentType, content }: OpsAppDocumentContent): Promise<OpsAppDocument> =>
        await dispatch(api.endpoints.replaceOpsAppDocument.initiate(
          { documentId, fileName, contentType, content: toBlob(content, contentType) }
        )).unwrap(),

      remove: async (documentId: string): Promise<boolean> => {
        const response = await dispatch(api.endpoints.deleteOpsAppDocument.initiate({ documentId })).unwrap();
        return response.deleted;
      },

      renderPdf: async ({ html, fileName, pageSize, marginPt }: RenderPdfInput): Promise<RenderPdfResult> => {
        const pdf = await renderHtmlToPdf(html, pageSize ?? "Letter", marginPt ?? 0);
        const document = await create({ fileName, contentType: "application/pdf", content: pdf });
        return { documentId: document.id, url: buildOpsAppDocumentDownloadUrl(document.id) };
      },

      download: async (documentId: string): Promise<void> => {
        requestOpsAppDocumentDownload(documentId);
      }
    };
  }, [dispatch]);
}
